/**
 * SecureEmbed Portal User Tier Management
 *
 * Manage portal user tier assignments across Energy Portal and other portals
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Loader2, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { userPoolApi } from '../services/api';

interface PoolUser {
  username: string;
  email?: string;
  status: string;
  group?: string;
  created?: string;
  lastModified?: string;
  poolId?: string;
  poolName?: string;
}

const TIER_OPTIONS = [
  { value: 'tier1', label: 'Tier 1', description: 'Basic search and analytics', color: 'bg-gray-100 text-gray-700' },
  { value: 'tier1a', label: 'Tier 1a', description: 'Insights and provenance', color: 'bg-blue-100 text-blue-700' },
  { value: 'tier2', label: 'Tier 2', description: 'Reports, export, and queries', color: 'bg-purple-100 text-purple-700' },
  { value: 'tier3', label: 'Tier 3', description: 'Full access including streaming', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'admin', label: 'Admin', description: 'All tiers plus admin controls', color: 'bg-red-100 text-red-700' },
];

const getTierBadge = (group?: string) => {
  const tier = TIER_OPTIONS.find(t => t.value === group) || TIER_OPTIONS[0];
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${tier.color}`}>
      {tier.label}
    </span>
  );
};

const PortalUsersPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const [users, setUsers] = useState<PoolUser[]>([]);
  const [pools, setPools] = useState<any[]>([]);
  const [selectedPool, setSelectedPool] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editTier, setEditTier] = useState<string>('');
  const [saving, setSaving] = useState(false);

  // ADO #4583 fix: this previously read `pool.id`, but the backend has
  // always returned `pool_id` — the selector's option values (and the
  // initial auto-select) were always `undefined`. Invisible before because
  // `pools.length > 1` was never true (only the local Hub pool existed);
  // now that Dev/Test pools are cross-account-listed too, a broken picker
  // would silently make it impossible to ever select anything but the
  // default pool. selectedPoolEnv is derived from the matching pool's `env`
  // (management/dev/test) and threaded through every user-management call
  // so writes land in the account the admin actually selected.
  const selectedPoolEnv = pools.find(p => p.pool_id === selectedPool)?.env as string | undefined;

  const loadPools = useCallback(async () => {
    try {
      const resp = await userPoolApi.listPools();
      if (resp?.pools) {
        setPools(resp.pools);
        if (resp.pools.length > 0 && !selectedPool) {
          setSelectedPool(resp.pools[0].pool_id);
        }
      }
    } catch {
      // Non-critical - use default pool
    }
  }, [selectedPool]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await userPoolApi.listUsers(selectedPool || undefined, selectedPoolEnv);
      if (resp?.users) {
        setUsers(resp.users.map((u: any) => ({
          username: u.username || u.Username,
          email: u.email || u.Attributes?.find((a: any) => a.Name === 'email')?.Value,
          status: u.status || u.UserStatus || 'UNKNOWN',
          group: u.group || u.groups?.[0] || 'tier1',
          created: u.created || u.UserCreateDate,
          lastModified: u.lastModified || u.UserLastModifiedDate,
          poolId: u.poolId || selectedPool,
          poolName: u.poolName,
        })));
      }
    } catch (err: any) {
      // LIVE DATA ONLY: never fabricate users. Surface the real failure and show
      // an empty/error state so an admin is never shown demo accounts as if real.
      setError(err.response?.data?.message || err.message || 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [selectedPool, selectedPoolEnv]);

  useEffect(() => {
    loadPools();
  }, [loadPools]);

  useEffect(() => {
    if (selectedPool || pools.length === 0) {
      loadUsers();
    }
  }, [selectedPool, loadUsers, pools.length]);

  const handleSaveTier = async (username: string) => {
    setSaving(true);
    setError(null);
    try {
      await userPoolApi.updateUser(username, {
        group: editTier,
        pool_id: selectedPool || undefined,
        env: selectedPoolEnv,
      });
      setUsers(prev => prev.map(u =>
        u.username === username ? { ...u, group: editTier } : u
      ));
      setSuccess(`Updated ${username} to ${TIER_OPTIONS.find(t => t.value === editTier)?.label || editTier}`);
      setTimeout(() => setSuccess(null), 3000);
      setEditingUser(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update user tier');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        {/* Title suppressed when embedded in the Users & Orgs hub. */}
        {!embedded && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Portal User Management</h2>
            <p className="text-gray-500 mt-1">Manage user access tiers across portals</p>
          </div>
        )}
        <button
          onClick={loadUsers}
          disabled={loading}
          className="ml-auto p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-3 rounded-lg">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 text-green-600 bg-green-50 px-4 py-3 rounded-lg">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{success}</span>
        </div>
      )}

      {/* Tier Legend */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4" /> Access Tier Reference
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {TIER_OPTIONS.map(tier => (
            <div key={tier.value} className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${tier.color}`}>{tier.label}</span>
              <span className="text-xs text-gray-500">{tier.description}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Pool Selector */}
      {pools.length > 1 && (
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">User Pool:</label>
          <select
            value={selectedPool}
            onChange={(e) => setSelectedPool(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            {pools.map(pool => (
              <option key={pool.pool_id} value={pool.pool_id}>
                {pool.env ? `[${pool.env}] ` : ''}{pool.name || pool.pool_id}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Access Tier</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map(user => (
                <tr key={user.username} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#0369A1] flex items-center justify-center text-white text-sm font-medium">
                        {user.username.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-gray-900">{user.username}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{user.email || '--'}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      user.status === 'CONFIRMED' ? 'bg-green-100 text-green-700' :
                      user.status === 'FORCE_CHANGE_PASSWORD' ? 'bg-amber-100 text-amber-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {editingUser === user.username ? (
                      <select
                        value={editTier}
                        onChange={(e) => setEditTier(e.target.value)}
                        className="px-2 py-1 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        {TIER_OPTIONS.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    ) : (
                      getTierBadge(user.group)
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {editingUser === user.username ? (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleSaveTier(user.username)}
                          disabled={saving}
                          className="px-3 py-1 text-xs bg-[#0369A1] text-white rounded-lg hover:bg-iflow-800 disabled:opacity-50"
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => setEditingUser(null)}
                          className="px-3 py-1 text-xs border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setEditingUser(user.username); setEditTier(user.group || 'tier1'); }}
                        className="px-3 py-1 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600"
                      >
                        Edit Tier
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-16 text-center text-sm text-gray-500">
                    {error ? 'Unable to load users — see the error above.' : 'No users found in this pool.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default PortalUsersPage;
